import axiosClient from "../api/axiosClient";

/**
 * Event Details Service
 * Endpoints for the promoter-facing Event Details page, ported from the
 * React Native app's event-detail-updated screen (src/Screens/event-detail-updated/*
 * + src/services/api/index.js + src/Actions/action.js in the mobile repo).
 */

// --- Event lifecycle ---
export const deleteEvent = (eventId) => axiosClient.post(`/Events/Delete/${eventId}`);

/**
 * Generic event field update, ported from mobile's onUpdateEventById —
 * reused by the watermark-event-details toggle and the join-requests
 * toggle (both just PUT a partial field set to /Events/{eventId}).
 */
export const updateEventDetails = (eventId, payload) =>
  axiosClient.put(`/Events/${eventId}`, JSON.stringify(payload));

// --- Watermark event details (per-event "show name/date on watermark" toggles) ---
/** @param {{ShowWatermarkEventName: boolean, ShowWatermarkEventTime: boolean}} settings */
export const updateWatermarkEventDetails = (eventId, settings) =>
  updateEventDetails(eventId, settings);

// --- Join requests toggle (Event Settings hub) ---
export const updateJoinRequestSetting = (eventId, isJoinRequestDisabled) =>
  updateEventDetails(eventId, { IsJoinRequestDisabled: isJoinRequestDisabled });

// --- Media ---
/** Recent gallery media across the whole event (not scoped to one album). */
export const getEventMedia = (eventId, { pageNumber = 1, pageSize = 20, orderBy = "desc" } = {}) =>
  axiosClient.get(`/Chat/${eventId}/Gallery/Media`, {
    params: { pageNumber, pageSize, OrderBy: orderBy },
  });

// --- Participants / sub-promoters / business assistants ---
export const getParticipants = (eventId, { pageNumber = 1, pageSize = 5 } = {}) =>
  axiosClient.get(`/Events/EventUsers/${eventId}`, {
    params: { pageNumber, pageSize, userType: "User" },
  });

export const getSubPromoters = (eventId) => axiosClient.get(`/Events/SubPromoters/${eventId}`);

export const getBusinessAssistants = (eventId, { pageNumber = 1, pageSize = 5 } = {}) =>
  axiosClient.get("/EventAssistant/GetAssistants", { params: { pageNumber, pageSize, eventId } });

export const removeSubPromoter = (eventId, subPromoterUserId) =>
  axiosClient.post(`/Events/RequestSubPromoterRemoval/?eventId=${eventId}&subPromoterId=${subPromoterUserId}`, {});

/**
 * Grants/revokes a Sub-Promoter's ability to generate/manage the event QR
 * (EventSubPromoter.CanManageQR — true by default at creation today, this is
 * what makes it actually toggleable by the promoter). Turning it off also
 * clears any linked device currently showing this Sub-Promoter's pQR
 * assignment (backend side effect, not duplicated here).
 */
export const setSubPromoterCanManageQR = (eventId, subPromoterUserId, canManageQR) =>
  axiosClient.post("/Events/SubPromoterCanManageQR", null, {
    params: { eventId, subPromoterId: subPromoterUserId, canManageQR },
  });

/** status: 1 | 2 | 3 (3 = deactivated) */
export const setSubPromoterStatus = (eventId, subPromoterUserId, status, reason = null) =>
  axiosClient.post("/Events/SubPromoterStatusWithReason", {
    eventId: Number(eventId),
    subPromoterId: subPromoterUserId,
    status,
    reason,
  });

export const removeBusinessAssistant = (assistantId) =>
  axiosClient.delete(`/EventAssistant/RemoveAssistant?assistantId=${assistantId}`);

/** Generates a shareable invite QR/link for business assistants to join this event. */
export const generateBusinessAssistantInvite = (eventId) =>
  axiosClient.get(`/EventAssistant/GenerateBAQRCode?EventId=${eventId}`);

/** Search the promoter's confirmed users to invite directly as a business assistant. */
export const searchConfirmedUsers = ({ pageNumber = 1, pageSize = 20, search = "" } = {}) =>
  axiosClient.get("/EventAssistant/ActiveConfirmedUsers", { params: { pageNumber, pageSize, search } });

/** Directly invites a confirmed user as a business assistant for this event. */
export const createBusinessAssistant = (eventId, assistantUserId) =>
  axiosClient.post("/EventAssistant/CreateAssistant", { eventId, AssistantUserId: assistantUserId, IsFullAccess: false });

export const blockKickParticipant = (chatRoomId, userId, { kick = false, block = true } = {}) =>
  axiosClient.post(`/Chat/Room/${chatRoomId}/User/${userId}/Block_Kick?kick=${kick}&block=${block}`, {});

export const blockUserFromChat = (chatRoomId, userId, isBlocked) =>
  axiosClient.post("/Chat/block-user-from-chat", { chatRoomId, UserIdToKick: userId, isBlocked });

// --- QR codes ---
// eQR (static Event QR) is never generated/displayed in the panel — it can only be
// requested as an emailed print version (PNG+PDF attachments sent to the caller's
// own account email). Dynamic/personalized QR (iQR) is app-only (must be generated
// on an authorized, location-verified device) and has no Promoter Panel surface at all.
export const emailEventQRCode = (eventId) => axiosClient.post(`/Events/email-qr-code?eventId=${eventId}&qrType=EQR`);

export const getWelcomeQR = (eventId) => axiosClient.get(`/Events/${eventId}/QR`);

export const getSubPromoterQR = (eventId) => axiosClient.get(`/Events/SubPromoterQR/${eventId}/`);
export const generateSubPromoterQR = (eventId) => axiosClient.post(`/Events/GenerateSubPromoterQR/${eventId}/`);

// Ticket QR ("T-Link") — generated at event creation next to eQR, but its
// per-event ticket threshold starts unset (TicketThreshold: null) until the
// promoter picks one; the QR/link only come back once a threshold is set.
export const getTicketQR = (eventId) => axiosClient.get(`/Events/${eventId}/tqr`);
export const setTicketQRThreshold = (eventId, ticketThreshold) =>
  axiosClient.post(`/Events/${eventId}/tqr/threshold`, { ticketThreshold });

// --- Business Assistant permissions ---
/** @param {string|number} assistantId @param {{canGenerateQr, canKickOrBlockUsers, canChatAsAssistant, canCreateGalleryAlbum, canViewGallery}} permissions */
export const updateAssistantPermissions = (assistantId, permissions) =>
  axiosClient.put(`/EventAssistant/UpdatePermissions?assistantId=${assistantId}`, permissions);

// --- Event hosts (reuses getParticipants above — see Phase 4 plan) ---
export const sendHostRequest = (eventId, targetUserId) =>
  axiosClient.post("/EventAssistant/SendAccessContactRequest", JSON.stringify({ chatRoomId: eventId, targetUserId }));

/** requestId is sent as the raw JSON body (not wrapped in an object) — matches mobile exactly. Used for both cancelling a pending request and removing an active host. */
export const cancelHostRequest = (requestId) =>
  axiosClient.post("/EventAssistant/CancelAccessContactRequest", JSON.stringify(requestId));

/** Updates all three Access Contact permissions in one atomic request. */
export const updateAccessContactPermissions = (requestId, permissions) =>
  axiosClient.post("/EventAssistant/SetAccessContactPermissions", {
    requestId,
    canManageQR: permissions.canManageQR,
    canManageChat: permissions.canManageChat,
    canKickOrBlockUsers: permissions.canKickOrBlockUsers,
  });

// --- Blocked / kicked users (per event) ---
export const getBlockedUsers = (eventId, { pageNumber = 1, pageSize = 20 } = {}) =>
  axiosClient.get("/Chat/BlockedOrKickedUsers", { params: { pageNumber, pageSize, filter: "blocked", eventId } });

export const unblockUser = (eventId, userId) =>
  axiosClient.post(`/Chat/UnblockOrUnkickUser?eventId=${eventId}&userId=${userId}&unblock=true`, {});

// --- Bulk participant actions ---
// New backend endpoints (see WebAPI's EventsController/EventAssistantController/
// ChatController) — each returns [{Id, Success, Error}] per item, not
// all-or-nothing, so a bad id in the batch doesn't sink the whole request.
export const bulkRemoveSubPromoters = (eventId, subPromoterIds) =>
  axiosClient.post("/Events/SubPromoters/Bulk/Remove", { eventId, subPromoterIds });

export const bulkRemoveBusinessAssistants = (assistantIds) =>
  axiosClient.delete("/EventAssistant/RemoveAssistantsBulk", { data: { assistantIds } });

export const bulkBlockKickParticipants = (chatRoomId, userIds, { kick = false, block = true } = {}) =>
  axiosClient.post(`/Chat/Room/${chatRoomId}/Users/Block_Kick/Bulk`, { userIds, kick, block });

export const bulkUnblockUsers = (eventId, userIds) =>
  axiosClient.post("/Chat/UnblockOrUnkickUsers/Bulk", { eventId, userIds, unblock: true, unkick: true });

// --- eQR buffer time ---
export const getEventQRBufferTime = (eventId) => axiosClient.get(`/Events/GetEventEQRTime/${eventId}`);

/** @param {{EventId, ValidBeforeEventStartHours, ValidAfterEventEndHours, IsActive}} payload */
export const updateEventQRBufferTime = (payload) =>
  axiosClient.post("/Events/UpdateEventEQRTime", JSON.stringify(payload));
